<template>
  <div class="mt-6">
    <!-- Abrir modal de Gerenciar Comodidades -->
    <v-card class="mb-6">
      <v-card-title>
        <v-icon left>
          {{ icons.mdiStarBox }}
        </v-icon>
        Gerenciar Comodidades
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="amenitiesDialog = true"
        >
          <v-icon left>
            {{ icons.mdiCog }}
          </v-icon>
          Abrir
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert
          type="info"
          text
          class="mb-0"
        >
          Abra o gerenciador para cadastrar, editar e organizar as comodidades.
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Configuração por Categoria -->
    <v-card>
      <v-card-title>
        <v-icon left>
          {{ icons.mdiDoor }}
        </v-icon>
        Configurar Categorias
      </v-card-title>
      <v-card-text>
        <v-alert
          type="info"
          text
          class="mb-4"
        >
          Configure descrição, imagens e comodidades para cada categoria de quarto.
          Estas informações serão exibidas no motor de reservas público.
        </v-alert>

        <v-data-table
          :headers="categoryHeaders"
          :items="categories"
          :loading="loadingCategories"
          class="elevation-1"
        >
          <template v-slot:item.cover_image="{ item }">
            <v-avatar
              size="60"
              tile
              rounded
              class="my-2"
            >
              <v-img
                v-if="item.cover_image"
                :src="item.cover_image"
                aspect-ratio="1.5"
              ></v-img>
              <v-icon
                v-else
                large
                color="grey lighten-1"
              >
                {{ icons.mdiImageOff }}
              </v-icon>
            </v-avatar>
          </template>

          <template v-slot:item.details_status="{ item }">
            <div class="d-flex flex-column">
              <v-chip
                x-small
                :color="item.has_description ? 'success' : 'grey'"
                text-color="white"
                class="mb-1"
              >
                <v-icon
                  x-small
                  left
                >
                  {{ icons.mdiText }}
                </v-icon>
                Descrição
              </v-chip>
              <v-chip
                x-small
                :color="item.images_count > 0 ? 'success' : 'grey'"
                text-color="white"
                class="mb-1"
              >
                <v-icon
                  x-small
                  left
                >
                  {{ icons.mdiImage }}
                </v-icon>
                {{ item.images_count }} imagens
              </v-chip>
              <v-chip
                x-small
                :color="item.amenities_count > 0 ? 'success' : 'grey'"
                text-color="white"
              >
                <v-icon
                  x-small
                  left
                >
                  {{ icons.mdiStar }}
                </v-icon>
                {{ item.amenities_count }} comodidades
              </v-chip>
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              color="primary"
              small
              :disabled="!canEdit"
              @click="openCategoryDetails(item)"
            >
              <v-icon
                left
                small
              >
                {{ icons.mdiCog }}
              </v-icon>
              Configurar
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog para criar/editar amenidade -->
    <v-dialog
      v-model="amenityDialog"
      max-width="500"
      persistent
    >
      <v-card>
        <v-card-title>
          {{ editingAmenity ? 'Editar Comodidade' : 'Nova Comodidade' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="amenityForm">
            <v-text-field
              v-model="amenityForm.name"
              label="Nome *"
              outlined
              dense
              :rules="[v => !!v || 'Nome é obrigatório']"
            ></v-text-field>

            <v-text-field
              v-model="amenityForm.icon"
              label="Ícone (MDI)"
              outlined
              dense
              hint="Ex: mdi-wifi, mdi-air-conditioner"
              persistent-hint
            >
              <template v-slot:append>
                <v-icon v-if="amenityForm.icon">
                  {{ amenityForm.icon }}
                </v-icon>
              </template>
            </v-text-field>

            <v-select
              v-model="amenityForm.group"
              :items="groupOptions"
              label="Grupo *"
              outlined
              dense
              :rules="[v => !!v || 'Grupo é obrigatório']"
            ></v-select>

            <v-text-field
              v-model.number="amenityForm.display_order"
              label="Ordem de Exibição"
              type="number"
              outlined
              dense
              min="0"
            ></v-text-field>

            <v-switch
              v-model="amenityForm.active"
              label="Ativo"
              color="success"
            ></v-switch>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeAmenityDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="savingAmenity"
            @click="saveAmenity"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Gerenciar Comodidades -->
    <v-dialog
      v-model="amenitiesDialog"
      max-width="1100"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title>
          <v-icon left>
            {{ icons.mdiStarBox }}
          </v-icon>
          Gerenciar Comodidades
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!canEdit"
            @click="openAmenityDialog()"
          >
            <v-icon left>
              {{ icons.mdiPlus }}
            </v-icon>
            Nova Comodidade
          </v-btn>
          <v-btn
            icon
            @click="amenitiesDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert
            type="info"
            text
            class="mb-4"
          >
            Configure as comodidades disponíveis no hotel. Estas opções poderão ser associadas às categorias de quarto.
          </v-alert>

          <v-data-table
            :headers="amenityHeaders"
            :items="filteredAmenities"
            :loading="loadingAmenities"
            class="elevation-1"
            :items-per-page="10"
          >
            <template v-slot:top>
              <v-card
                class="filters-card"
                outlined
              >
                <v-card-text class="py-3">
                  <div class="d-flex align-center flex-wrap">
                    <div class="filters-title">
                      <v-icon
                        small
                        class="mr-2"
                      >
                        {{ icons.mdiTune }}
                      </v-icon>
                      Filtros
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      small
                      class="filters-clear"
                      @click="clearAmenityFilters"
                    >
                      Limpar
                    </v-btn>
                  </div>
                  <v-row
                    dense
                    class="mt-2"
                  >
                    <v-col
                      cols="12"
                      md="7"
                    >
                      <v-text-field
                        v-model="amenitySearch"
                        :prepend-inner-icon="icons.mdiMagnify"
                        label="Buscar comodidade..."
                        outlined
                        dense
                        hide-details
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="5"
                    >
                      <v-select
                        v-model="amenityGroupFilter"
                        :items="amenityGroupOptions"
                        label="Grupo"
                        outlined
                        dense
                        hide-details
                        clearable
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </template>

            <template v-slot:item.icon="{ item }">
              <v-icon v-if="resolveMdiIcon(item.icon)">
                {{ resolveMdiIcon(item.icon) }}
              </v-icon>
              <span
                v-else
                class="text--disabled"
              >-</span>
            </template>

            <template v-slot:item.group="{ item }">
              <v-chip
                small
                :color="getGroupColor(item.group)"
                text-color="white"
              >
                {{ groups[item.group] || item.group }}
              </v-chip>
            </template>

            <template v-slot:item.active="{ item }">
              <v-switch
                :input-value="item.active"
                color="success"
                dense
                hide-details
                :disabled="!canEdit"
                @change="toggleAmenityActive(item)"
              ></v-switch>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                :disabled="!canEdit"
                @click="openAmenityDialog(item)"
              >
                <v-icon small>
                  {{ icons.mdiPencilOutline }}
                </v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                :disabled="!canEdit"
                @click="deleteAmenity(item)"
              >
                <v-icon small>
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal de Detalhes da Categoria -->
    <category-details-modal
      v-model="categoryDetailsDialog"
      :category="selectedCategory"
      :amenities="amenities"
      :groups="groups"
      :can-edit="canEdit"
      @saved="loadCategories"
    />
  </div>
</template>

<script>
import * as mdiIcons from '@mdi/js'
import {
  mdiCog,
  mdiClose,
  mdiDeleteOutline,
  mdiDoor,
  mdiImage,
  mdiImageOff,
  mdiMagnify,
  mdiPencilOutline,
  mdiPlus,
  mdiStar,
  mdiStarBox,
  mdiText,
  mdiTune,
} from '@mdi/js'
import CategoryDetailsModal from './CategoryDetailsModal.vue'

export default {
  name: 'AmenitiesConfigTab',

  components: {
    CategoryDetailsModal,
  },

  props: {
    canEdit: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    loadingAmenities: false,
    loadingCategories: false,
    amenities: [],
    categories: [],
    groups: {},
    amenitySearch: '',
    amenityGroupFilter: null,

    amenityDialog: false,
    amenitiesDialog: false,
    editingAmenity: null,
    savingAmenity: false,
    amenityForm: {
      name: '',
      icon: '',
      group: 'general',
      display_order: 0,
      active: true,
    },

    categoryDetailsDialog: false,
    selectedCategory: null,
    icons: {
      mdiCog,
      mdiClose,
      mdiDeleteOutline,
      mdiDoor,
      mdiImage,
      mdiImageOff,
      mdiMagnify,
      mdiPencilOutline,
      mdiPlus,
      mdiStar,
      mdiStarBox,
      mdiText,
      mdiTune,
    },

    amenityHeaders: [
      {
        text: 'Ícone', value: 'icon', sortable: false, width: '80px',
      },
      { text: 'Nome', value: 'name' },
      { text: 'Grupo', value: 'group', width: '150px' },
      { text: 'Ordem', value: 'display_order', width: '100px' },
      { text: 'Ativo', value: 'active', width: '100px' },
      {
        text: 'Ações', value: 'actions', sortable: false, width: '120px', align: 'center',
      },
    ],

    categoryHeaders: [
      {
        text: 'Imagem', value: 'cover_image', sortable: false, width: '100px',
      },
      { text: 'Categoria', value: 'name' },
      {
        text: 'Status', value: 'details_status', sortable: false, width: '150px',
      },
      {
        text: 'Ações', value: 'actions', sortable: false, width: '150px', align: 'center',
      },
    ],

    groupColors: {
      bathroom: 'info',
      view: 'teal',
      room: 'orange',
      general: 'purple',
      policy: 'warning',
    },
  }),

  computed: {
    groupOptions() {
      return Object.entries(this.groups).map(([value, text]) => ({
        value,
        text,
      }))
    },
    amenityGroupOptions() {
      return this.groupOptions
    },
    filteredAmenities() {
      const search = (this.amenitySearch || '').toLowerCase().trim()

      return this.amenities.filter(amenity => {
        const matchesName = !search || (amenity.name || '').toLowerCase().includes(search)
        const matchesGroup = !this.amenityGroupFilter || amenity.group === this.amenityGroupFilter

        return matchesName && matchesGroup
      })
    },
  },

  mounted() {
    this.loadAmenities()
    this.loadCategories()
  },

  methods: {
    clearAmenityFilters() {
      this.amenitySearch = ''
      this.amenityGroupFilter = null
    },
    async loadAmenities() {
      this.loadingAmenities = true
      try {
        const response = await this.$http.get('system/booking-engine-config/amenities')
        if (response.data.success) {
          this.amenities = response.data.data.amenities
          this.groups = response.data.data.groups
        }
      } catch (error) {
        console.error('Erro ao carregar amenidades:', error)
        this.$toast.error('Erro ao carregar comodidades')
      } finally {
        this.loadingAmenities = false
      }
    },

    async loadCategories() {
      this.loadingCategories = true
      try {
        const response = await this.$http.get('system/booking-engine-config/room-categories')
        if (response.data.success) {
          // Para cada categoria, buscar os detalhes
          const categoriesWithDetails = await Promise.all(
            response.data.data.categories.map(async cat => {
              try {
                const detailsResponse = await this.$http.get(
                  `system/booking-engine-config/room-categories/${cat.id}/details`,
                )
                const details = detailsResponse.data.data

                return {
                  ...cat,
                  cover_image: details.images?.find(img => img.is_cover)?.url
                    || details.images?.[0]?.url || null,
                  images_count: details.images?.length || 0,
                  amenities_count: details.amenities?.length || 0,
                  has_description: !!details.detailed_description,
                }
              } catch {
                return {
                  ...cat,
                  cover_image: null,
                  images_count: 0,
                  amenities_count: 0,
                  has_description: false,
                }
              }
            }),
          )
          this.categories = categoriesWithDetails
        }
      } catch (error) {
        console.error('Erro ao carregar categorias:', error)
        this.$toast.error('Erro ao carregar categorias')
      } finally {
        this.loadingCategories = false
      }
    },

    getGroupColor(group) {
      return this.groupColors[group] || 'grey'
    },
    resolveMdiIcon(name) {
      if (!name) return null
      if (name.startsWith('mdi')) {
        if (!name.includes('-')) return mdiIcons[name] || null
        const key = `mdi${name
          .replace(/^mdi-/, '')
          .split('-')
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join('')}`

        return mdiIcons[key] || null
      }

      return null
    },

    openAmenityDialog(amenity = null) {
      this.editingAmenity = amenity
      if (amenity) {
        this.amenityForm = { ...amenity }
      } else {
        this.amenityForm = {
          name: '',
          icon: '',
          group: 'general',
          display_order: 0,
          active: true,
        }
      }
      this.amenityDialog = true
    },

    closeAmenityDialog() {
      this.amenityDialog = false
      this.editingAmenity = null
      this.$refs.amenityForm?.reset()
    },

    async saveAmenity() {
      if (!this.$refs.amenityForm.validate()) return

      this.savingAmenity = true
      try {
        if (this.editingAmenity) {
          await this.$http.put(
            `system/booking-engine-config/amenities/${this.editingAmenity.id}`,
            this.amenityForm,
          )
          this.$toast.success('Comodidade atualizada com sucesso')
        } else {
          await this.$http.post('system/booking-engine-config/amenities', this.amenityForm)
          this.$toast.success('Comodidade criada com sucesso')
        }
        this.closeAmenityDialog()
        this.loadAmenities()
      } catch (error) {
        console.error('Erro ao salvar amenidade:', error)
        this.$toast.error('Erro ao salvar comodidade')
      } finally {
        this.savingAmenity = false
      }
    },

    async toggleAmenityActive(amenity) {
      try {
        await this.$http.put(`system/booking-engine-config/amenities/${amenity.id}`, {
          active: !amenity.active,
        })
        amenity.active = !amenity.active
      } catch (error) {
        console.error('Erro ao atualizar amenidade:', error)
        this.$toast.error('Erro ao atualizar comodidade')
      }
    },

    async deleteAmenity(amenity) {
      const confirmed = await this.$swal.fire({
        title: 'Confirmar exclusão',
        text: `Deseja remover a comodidade "${amenity.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover',
        cancelButtonText: 'Cancelar',
      })

      if (!confirmed.isConfirmed) return

      try {
        await this.$http.delete(`system/booking-engine-config/amenities/${amenity.id}`)
        this.$toast.success('Comodidade removida com sucesso')
        this.loadAmenities()
      } catch (error) {
        console.error('Erro ao remover amenidade:', error)
        this.$toast.error('Erro ao remover comodidade')
      }
    },

    openCategoryDetails(category) {
      this.selectedCategory = category
      this.categoryDetailsDialog = true
    },
  },
}
</script>

<style scoped>
.filters-card {
  border: 1px dashed rgba(0, 0, 0, 0.15);
  background: #fafafa;
}

.filters-title {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
}

.filters-clear {
  color: #16b1ff;
}
</style>
